import React from "react"

import { Card } from "@/components/ui/card"
import { Combobox } from "@/components/ui/combobox"

import PDF from "../PDF"
import Transcription, { TranscriptionNot } from "./Transcription"
import { useOrder } from "../../../contexts/OrderContext"
import { getFileNameFromUrl, isImageFilename } from "@/lib/utils"
import Suggestions from "./Suggestions"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DownloadIcon, FileStackIcon, LoaderCircleIcon } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { useParams } from "react-router-dom"
import { useDocuments } from "@/contexts/DocumentsContext"
import { supabase } from "@/lib/supabase"
import { OrderLoader } from "../Order"
import { ReportDocument } from "./Submission"
import ReportForm from "./ReportForm"
import Guide from "@/Guide"

const { APP_API_URL } = import.meta.env

export default function Documents({ defaultDocument }) {
    const { order, setOrder } = useOrder()
    const { documents, setDocument: setDocumentCtx, document: documentCtx } = useDocuments()
    const { session } = useAuth()
    const { id, page } = useParams()

    const [document, setDocumentState] = React.useState(defaultDocument || documentCtx)

    const setDocument = (value) => {
        setDocumentState(value)
    }

    React.useEffect(() => {
        if (!defaultDocument && !!order?.mandate?.path) {
            setDocument(documentCtx)
        }
    }, [documentCtx])


    const [loadingMandate, setLoadingMandate] = React.useState(false)

    const selectMandate = async () => {
        setLoadingMandate(true)

        const mandate_storage_path = document

        const { error } = await fetch(`${APP_API_URL}/generation/mandate_file/${order.pydio_id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ mandate_storage_path }),
        }).then(res => res.json())

        if (error) {
            return alert(error.message)
        }

        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                path: mandate_storage_path,
                mandate_storage_path
            }
        })
    }

    const isViewer = React.useMemo(() => document && (isImageFilename(document) || document.toLowerCase().includes('.pdf')), [document])

    return <div className="min-h-0 flex flex-col gap-2 w-full flex-grow">
        {!order?.mandate?.path && <div className="min-h-16 relative overflow-visible flex items-center gap-2 w-full">
            <Guide step={'select_mandate'} />
        </div>}
        
        <div className="flex flex-grow flex-col gap-2 min-h-0">
            <div className="flex items-center">
                <Combobox
                    options={documents || []}
                    value={document}
                    onChange={(value) => setDocument(value)}
                    emptyText="Aucun document trouvé."
                    searchText="Rechercher un document..."
                    selectText="Selectionner un document ..."
                />

                {!order?.mandate?.path && <div>
                    <Button className="ml-2" onClick={selectMandate} disabled={loadingMandate}>
                        {loadingMandate && (
                            <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                        )}

                        Sélectionner le mandat
                    </Button>
                </div>}
            </div>

            {!!document && <>
                {document === 'transcription' && <Transcription />}

                {document === 'report' && <>
                    {page === 'report' && <ReportForm />}
                    {page === 'validation' && <ReportDocument />}
                </>}

                {!['transcription', 'transcription_not', 'suggestions', 'report'].includes(document) && <>
                    {isViewer && <PDF path={document} />}
                    {!isViewer && <File path={document} />}
                </>}
            </>}
        </div>
    </div>
}

function File({ path }) {
    const [loading, setLoading] = React.useState(false)
    const { order } = useOrder()

    const fetchUri = async () => {
        setLoading(true)

        const { data: { signedUrl } } = await supabase.storage.from("folders").createSignedUrl(
            `${path}`,
            60
        )

        setLoading(false)

        window.open(signedUrl, '_blank')
    };

    return <div className="flex items-center justify-center p-4">
        <Button onClick={fetchUri}>
            {loading && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
            )}

            {!loading && <DownloadIcon className="mr-2 h-4 w-4 mr-2" />}

            Télécharger le fichier
        </Button>
    </div>
}